/**
 * Button — single primitive used everywhere there's a clickable action.
 *
 * Variants extracted from FOM patterns:
 *   primary  — blue solid (Add, Save, Done)
 *   ghost    — transparent over var(--bg-elevated) (Mark OOS, secondary actions)
 *   danger   — red, used for Reset Shift / destructive
 *   link     — underline, no background (Edit Equipment, Activate)
 *
 * Sizes:
 *   sm — 4px 10px, 12px font
 *   md — 5px 12px, 13px font
 */
import type { CSSProperties, ReactNode, MouseEvent } from 'react'

type Variant = 'primary' | 'ghost' | 'danger' | 'link'
type Size = 'sm' | 'md'

interface ButtonProps {
  variant?: Variant
  size?: Size
  disabled?: boolean
  title?: string
  type?: 'button' | 'submit'
  style?: CSSProperties
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  children: ReactNode
}

const VARIANTS: Record<Variant, CSSProperties> = {
  primary: {
    background: '#2563eb',
    border: 'none',
    color: '#fff',
    fontWeight: 600,
  },
  ghost: {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    fontWeight: 500,
  },
  danger: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#ef4444',
    fontWeight: 700,
  },
  link: {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    fontWeight: 500,
    textDecoration: 'underline',
    padding: 0,
  },
}

const SIZES: Record<Size, CSSProperties> = {
  sm: { padding: '4px 10px', fontSize: 12, borderRadius: 6 },
  md: { padding: '5px 12px', fontSize: 13, borderRadius: 8 },
}

export default function Button({
  variant = 'primary',
  size = 'sm',
  disabled = false,
  title,
  type = 'button',
  style,
  onClick,
  children,
}: ButtonProps) {
  const base: CSSProperties = {
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    opacity: disabled ? 0.5 : 1,
    transition: 'background 0.15s',
  }
  const variantStyle = VARIANTS[variant]
  const sizeStyle = variant === 'link' ? {} : SIZES[size]

  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{ ...base, ...variantStyle, ...sizeStyle, ...style }}
    >
      {children}
    </button>
  )
}
